import './styles.css';

type Phase = 'intro' | 'play' | 'upgrade' | 'over';
type EnemyKind = 'drone' | 'swarm' | 'crystal' | 'brute';
type UpgradeId = 'splash' | 'chain' | 'magnet' | 'shield' | 'storm' | 'split';

type Orb = {
  id: number;
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  held: boolean;
  ready: boolean;
  trail: { x: number; y: number }[];
  lastX: number;
  lastY: number;
  age: number;
};

type Enemy = {
  id: number;
  kind: EnemyKind;
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  hp: number;
  maxHp: number;
  wiggle: number;
  hit: number;
};

type Burst = { x: number; y: number; r: number; max: number; life: number; kind: 'splash' | 'hit' | 'core' };
type Particle = { x: number; y: number; vx: number; vy: number; life: number; color: string; size: number };
type Pop = { x: number; y: number; text: string; life: number; color: string };
type Upgrade = { id: UpgradeId; name: string; text: string; apply: () => void };

type State = {
  phase: Phase;
  wave: number;
  hp: number;
  maxHp: number;
  score: number;
  best: number;
  combo: number;
  comboClock: number;
  orbsLeft: number;
  spawnedThisWave: number;
  enemiesToSpawn: number;
  spawnClock: number;
  orbClock: number;
  nextId: number;
  held?: Orb;
  orbs: Orb[];
  enemies: Enemy[];
  bursts: Burst[];
  particles: Particle[];
  pops: Pop[];
  last: number;
  splash: number;
  chain: number;
  magnet: number;
  shield: number;
  split: boolean;
  storm: boolean;
};

const app = document.querySelector<HTMLDivElement>('#app')!;
app.innerHTML = `<main class="shell">
  <section id="intro" class="overlay">
    <div class="card hero">
      <p class="kicker">Grab · Throw · Chain</p>
      <h1>Monsoon Circuit</h1>
      <p>Catch falling storm drops, fling them into the drone swarm, and keep the rooftop core alive for one more wave.</p>
      <div class="beats"><span></span><span></span><span></span></div>
      <button id="start" class="primary">Start rain run</button>
    </div>
  </section>
  <section class="top">
    <div><b>Monsoon Circuit</b><span id="sub">Catch & chuck storm arcade</span></div>
    <button id="restart">Restart</button>
  </section>
  <section class="stats">
    <div><span>Core</span><b id="hp"></b></div>
    <div><span>Wave</span><b id="wave"></b></div>
    <div><span>Combo</span><b id="combo"></b></div>
    <div><span>Score</span><b id="score"></b></div>
  </section>
  <section class="goal"><b id="goalTitle">Catch a storm drop</b><p id="goalText">Drag near a falling drop to grab it. Release with a flick to splash the swarm.</p></section>
  <canvas id="game" aria-label="Monsoon Circuit catch and throw arcade canvas"></canvas>
  <section class="bar"><p id="hint">One finger. Grab a glowing drop, chuck it into the red swarm, chain the splash.</p></section>
  <section id="upgrade" class="overlay" hidden><div class="card"><p class="kicker">Wave clear</p><h2>Pick a storm trick</h2><div id="choices"></div></div></section>
  <section id="over" class="overlay" hidden><div class="card"><p class="kicker">Run ended</p><h2 id="overTitle">Core flooded</h2><p id="overText"></p><button id="again" class="primary">Another run</button></div></section>
</main>`;

const canvas = document.querySelector<HTMLCanvasElement>('#game')!;
const ctx = canvas.getContext('2d')!;
let W = 0;
let H = 0;
let DPR = 1;
let st: State;

const $ = <T extends HTMLElement>(sel: string) => document.querySelector<T>(sel)!;
const core = () => ({ x: W * 0.5, y: H - 62, r: 30 });

function fit() {
  DPR = Math.min(devicePixelRatio || 1, 2);
  const rect = canvas.getBoundingClientRect();
  W = rect.width;
  H = rect.height;
  canvas.width = Math.floor(W * DPR);
  canvas.height = Math.floor(H * DPR);
  ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
}

function hide(id: string) { $('#' + id).hidden = true; }
function show(id: string) { $('#' + id).hidden = false; }
function rand(a: number, b: number) { return a + Math.random() * (b - a); }
function dist(a: { x: number; y: number }, b: { x: number; y: number }) { return Math.hypot(a.x - b.x, a.y - b.y); }

function init() {
  fit();
  const best = Number(localStorage.getItem('monsoon-best') || 0);
  st = {
    phase: 'play', wave: 1, hp: 7, maxHp: 7, score: 0, best, combo: 0, comboClock: 0,
    orbsLeft: 0, spawnedThisWave: 0, enemiesToSpawn: 0, spawnClock: 0, orbClock: 0, nextId: 1,
    orbs: [], enemies: [], bursts: [], particles: [], pops: [], last: performance.now(),
    splash: 58, chain: 2, magnet: 70, shield: 0, split: false, storm: false,
  };
  startWave();
  hide('intro'); hide('over'); hide('upgrade');
  setText();
  requestAnimationFrame(loop);
}

function startWave() {
  st.phase = 'play';
  st.enemies = [];
  st.orbs = [];
  st.held = undefined;
  st.orbsLeft = 4 + Math.min(3, Math.floor(st.wave / 2));
  st.spawnedThisWave = 0;
  st.enemiesToSpawn = 8 + st.wave * 4;
  st.spawnClock = 0.15;
  st.orbClock = 0.25;
  pop(W * 0.5, 72, `wave ${st.wave}`, '#bffbff');
  shake(10);
}

function setText() {
  $('#sub').textContent = `Catch & chuck · best ${st.best}`;
  $('#hp').textContent = `${st.hp}/${st.maxHp}${st.shield ? ` +${st.shield}` : ''}`;
  $('#wave').textContent = String(st.wave);
  $('#combo').textContent = st.combo ? `x${st.combo}` : '—';
  $('#score').textContent = String(st.score);
  const title = st.phase === 'upgrade' ? 'Choose a trick' : st.held ? 'Flick to throw' : st.orbs.length ? 'Grab the glowing drop' : 'Survive the swarm';
  const text = st.phase === 'upgrade' ? 'One upgrade changes the next wave.' : st.held ? 'Release in the direction of your swipe. Direct hits splash harder.' : st.orbs.length ? 'Drag near a drop. It sticks to your finger immediately.' : 'More drops are falling. Keep drones away from the rooftop core.';
  $('#goalTitle').textContent = title;
  $('#goalText').textContent = text;
}

function spawnOrb() {
  if (st.orbsLeft <= 0) return;
  st.orbsLeft--;
  st.orbs.push({
    id: st.nextId++, x: rand(44, W - 44), y: -24, vx: rand(-18, 18), vy: rand(76, 105), r: 14,
    held: false, ready: true, trail: [], lastX: 0, lastY: 0, age: 0,
  });
}

function spawnEnemy() {
  if (st.spawnedThisWave >= st.enemiesToSpawn) return;
  st.spawnedThisWave++;
  const roll = Math.random();
  let kind: EnemyKind = 'drone';
  if (st.wave >= 4 && roll < 0.12) kind = 'brute';
  else if (st.wave >= 2 && roll < 0.34) kind = 'swarm';
  else if (st.wave >= 3 && roll < 0.48) kind = 'crystal';
  const r = kind === 'brute' ? 24 : kind === 'crystal' ? 18 : kind === 'swarm' ? 10 : 15;
  const hp = kind === 'brute' ? 4 : kind === 'crystal' ? 2 : 1;
  const side = Math.floor(rand(0, 3));
  let x = rand(30, W - 30), y = 20;
  if (side === 1) { x = -25; y = rand(68, H * 0.64); }
  if (side === 2) { x = W + 25; y = rand(68, H * 0.64); }
  const c = core();
  const a = Math.atan2(c.y - y, c.x - x);
  const speed = (kind === 'swarm' ? 64 : kind === 'brute' ? 28 : 42) + st.wave * 2.6;
  st.enemies.push({ id: st.nextId++, kind, x, y, vx: Math.cos(a) * speed, vy: Math.sin(a) * speed, r, hp, maxHp: hp, wiggle: rand(0, 20), hit: 0 });
}

function pop(x: number, y: number, text: string, color = '#9effbf') {
  st.pops.push({ x, y, text, color, life: 1 });
  st.pops = st.pops.slice(-24);
}

let shakePower = 0;
function shake(n: number) { shakePower = Math.max(shakePower, n); }

function addParticles(x: number, y: number, color: string, n = 12) {
  for (let i = 0; i < n; i++) {
    const a = rand(0, Math.PI * 2), s = rand(40, 220);
    st.particles.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: rand(0.35, 0.9), color, size: rand(2, 5) });
  }
}

function splash(x: number, y: number, direct: Enemy | undefined, big = false) {
  const radius = st.splash * (big ? 1.18 : 1);
  st.bursts.push({ x, y, r: 0, max: radius, life: 1, kind: 'splash' });
  addParticles(x, y, '#74f7ff', 20);
  shake(8);
  const candidates = st.enemies
    .map(e => ({ e, d: Math.hypot(e.x - x, e.y - y) }))
    .filter(o => o.d < radius + o.e.r)
    .sort((a, b) => a.d - b.d)
    .slice(0, 1 + st.chain);
  if (direct && !candidates.some(o => o.e === direct)) candidates.unshift({ e: direct, d: 0 });
  let kills = 0;
  candidates.forEach((o, i) => { if (damageEnemy(o.e, i === 0 && direct === o.e ? 2 : 1, x, y)) kills++; });
  if (kills >= 3) { st.score += kills * 15; pop(x, y - 18, `chain ${kills}`, '#ffd166'); }
}

function damageEnemy(e: Enemy, dmg: number, sx: number, sy: number) {
  e.hp -= dmg;
  e.hit = 0.2;
  st.bursts.push({ x: e.x, y: e.y, r: e.r, max: e.r + 16, life: 0.45, kind: 'hit' });
  if (e.hp > 0) return false;
  const base = e.kind === 'brute' ? 120 : e.kind === 'crystal' ? 70 : e.kind === 'swarm' ? 18 : 35;
  st.comboClock = 2.6;
  st.combo++;
  const bonus = st.combo >= 5 ? Math.floor(base * 0.7) : 0;
  st.score += base + bonus;
  pop(e.x, e.y - 8, `+${base + bonus}`, bonus ? '#ffd166' : '#9effbf');
  addParticles(e.x, e.y, e.kind === 'crystal' ? '#ffd166' : '#ff7b93', e.kind === 'brute' ? 26 : 12);
  st.enemies = st.enemies.filter(x => x !== e);
  if (e.kind === 'crystal' && Math.random() < 0.8) {
    st.orbsLeft++;
    st.orbClock = Math.min(st.orbClock, 0.18);
  }
  if (st.storm && Math.random() < 0.18) splash(e.x, e.y, undefined, false);
  // subtle chain line particles from impact source
  for (let i = 0; i < 4; i++) st.particles.push({ x: sx + (e.x - sx) * rand(.2, .8), y: sy + (e.y - sy) * rand(.2, .8), vx: 0, vy: 0, life: .25, color: '#dffcff', size: 2 });
  return true;
}

function coreHit(e: Enemy) {
  if (st.shield > 0) { st.shield--; pop(core().x, core().y - 34, 'shield', '#bffbff'); }
  else { st.hp--; st.combo = 0; pop(core().x, core().y - 34, 'hit', '#ff9a9a'); }
  st.bursts.push({ x: core().x, y: core().y, r: 6, max: 70, life: 1, kind: 'core' });
  addParticles(core().x, core().y, '#ff7b93', 22);
  shake(18);
  st.enemies = st.enemies.filter(x => x !== e);
  if (st.hp <= 0) gameOver();
}

function update(dt: number) {
  if (st.phase !== 'play') return;
  st.spawnClock -= dt;
  st.orbClock -= dt;
  if (st.spawnClock <= 0) { spawnEnemy(); st.spawnClock = Math.max(0.28, 1.05 - st.wave * 0.07) * rand(0.72, 1.2); }
  if (st.orbClock <= 0) { spawnOrb(); st.orbClock = rand(1.05, 1.7); }

  if (st.comboClock > 0) st.comboClock -= dt;
  else st.combo = 0;

  const c = core();
  for (const e of [...st.enemies]) {
    e.wiggle += dt * 5;
    const angle = Math.atan2(c.y - e.y, c.x - e.x);
    const wig = e.kind === 'swarm' ? Math.sin(e.wiggle * 2.3) * 34 : Math.sin(e.wiggle) * 12;
    e.vx += Math.cos(angle) * dt * 18 + Math.cos(angle + Math.PI / 2) * wig * dt;
    e.vy += Math.sin(angle) * dt * 18 + Math.sin(angle + Math.PI / 2) * wig * dt;
    const max = e.kind === 'swarm' ? 92 : e.kind === 'brute' ? 42 : 64 + st.wave * 2;
    const m = Math.hypot(e.vx, e.vy) || 1;
    if (m > max) { e.vx = e.vx / m * max; e.vy = e.vy / m * max; }
    e.x += e.vx * dt; e.y += e.vy * dt;
    e.hit = Math.max(0, e.hit - dt);
    if (dist(e, c) < e.r + c.r * 0.7) coreHit(e);
  }

  for (const o of [...st.orbs]) {
    o.age += dt;
    if (!o.held) {
      if (st.magnet > 0 && st.held == null) {
        // passive magnet is handled on pointer move; falling drops remain readable.
      }
      o.vy += 96 * dt;
      o.x += o.vx * dt;
      o.y += o.vy * dt;
      if (o.x < o.r || o.x > W - o.r) o.vx *= -0.75;
      if (o.y > H + 50) st.orbs = st.orbs.filter(x => x !== o);
      for (const e of [...st.enemies]) {
        if (dist(o, e) < o.r + e.r) {
          splash(o.x, o.y, e, true);
          st.orbs = st.orbs.filter(x => x !== o);
          if (st.split) { splash(o.x - 36, o.y + 8, undefined, false); splash(o.x + 36, o.y + 8, undefined, false); }
          break;
        }
      }
    }
    o.trail.push({ x: o.x, y: o.y });
    o.trail = o.trail.slice(-10);
  }

  st.bursts = st.bursts.map(b => ({ ...b, r: b.r + (b.max - b.r) * dt * 9, life: b.life - dt * 1.8 })).filter(b => b.life > 0);
  st.particles = st.particles.map(p => ({ ...p, x: p.x + p.vx * dt, y: p.y + p.vy * dt, vy: p.vy + 180 * dt, life: p.life - dt })).filter(p => p.life > 0);
  st.pops = st.pops.map(p => ({ ...p, y: p.y - dt * 26, life: p.life - dt * 1.4 })).filter(p => p.life > 0);

  if (st.spawnedThisWave >= st.enemiesToSpawn && st.enemies.length === 0 && st.orbs.length === 0 && st.phase === 'play') clearWave();
  setText();
}

function clearWave() {
  st.score += 160 + st.wave * 40 + st.hp * 10;
  pop(W * 0.5, H * 0.34, 'clear', '#ffd166');
  if (st.wave % 1 === 0) showUpgrades();
}

function upgradePool(): Upgrade[] {
  return [
    { id: 'splash', name: 'Larger Splash', text: 'Storm drops burst wider.', apply: () => { st.splash += 16; } },
    { id: 'chain', name: 'Chain Count', text: 'Lightning jumps to one more target.', apply: () => { st.chain += 1; } },
    { id: 'magnet', name: 'Magnetic Grab', text: 'Drops snap to your finger from farther away.', apply: () => { st.magnet += 45; } },
    { id: 'shield', name: 'Storm Shield', text: 'Block the next two core hits.', apply: () => { st.shield += 2; } },
    { id: 'storm', name: 'Rainmaker', text: 'Some kills trigger tiny bonus splashes.', apply: () => { st.storm = true; } },
    { id: 'split', name: 'Split Splash', text: 'Thrown drops burst with two side shocks.', apply: () => { st.split = true; } },
  ];
}

function showUpgrades() {
  st.phase = 'upgrade';
  const choices = upgradePool().sort(() => Math.random() - 0.5).slice(0, 3);
  $('#choices').innerHTML = choices.map(u => `<button class="choice" data-id="${u.id}"><b>${u.name}</b><span>${u.text}</span></button>`).join('');
  show('upgrade');
  $('#choices').querySelectorAll<HTMLButtonElement>('.choice').forEach(btn => {
    btn.onclick = () => {
      const u = choices.find(x => x.id === btn.dataset.id)!;
      u.apply();
      st.wave++;
      hide('upgrade');
      startWave();
      setText();
    };
  });
  setText();
}

function gameOver() {
  st.phase = 'over';
  st.best = Math.max(st.best, st.score);
  localStorage.setItem('monsoon-best', String(st.best));
  $('#overTitle').textContent = 'Rooftop core flooded';
  $('#overText').textContent = `Score ${st.score} · Wave ${st.wave} · Best ${st.best}`;
  show('over');
  setText();
}

function drawDrop(x: number, y: number, r: number, held = false) {
  ctx.save();
  ctx.translate(x, y);
  ctx.shadowColor = '#74f7ff'; ctx.shadowBlur = held ? 34 : 22;
  const g = ctx.createRadialGradient(-r * .2, -r * .5, 1, 0, 0, r * 1.2);
  g.addColorStop(0, '#ffffff'); g.addColorStop(.32, '#9effff'); g.addColorStop(1, '#1ccbd4');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.moveTo(0, -r * 1.35);
  ctx.bezierCurveTo(r * 1.05, -r * .25, r * .72, r * .95, 0, r * 1.05);
  ctx.bezierCurveTo(-r * .72, r * .95, -r * 1.05, -r * .25, 0, -r * 1.35);
  ctx.fill();
  ctx.globalAlpha = .42; ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.stroke();
  ctx.restore();
}

function drawEnemy(e: Enemy) {
  ctx.save(); ctx.translate(e.x, e.y);
  ctx.shadowColor = e.kind === 'crystal' ? '#ffd166' : '#ff6b7e'; ctx.shadowBlur = 14 + e.hit * 80;
  if (e.kind === 'crystal') {
    ctx.rotate(Math.sin(e.wiggle) * .1);
    ctx.fillStyle = '#ffd166';
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = -Math.PI / 2 + i * Math.PI / 3;
      const rr = i % 2 ? e.r * .78 : e.r * 1.05;
      const x = Math.cos(a) * rr, y = Math.sin(a) * rr;
      i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
    }
    ctx.closePath(); ctx.fill();
    ctx.fillStyle = '#fff4bd'; ctx.fillRect(-3, -e.r * .52, 6, e.r);
  } else {
    ctx.fillStyle = e.kind === 'brute' ? '#ff9a57' : e.kind === 'swarm' ? '#ff4770' : '#ff6575';
    ctx.beginPath();
    if (e.kind === 'brute') ctx.roundRect(-e.r, -e.r * .72, e.r * 2, e.r * 1.44, 8);
    else ctx.ellipse(0, 0, e.r * 1.15, e.r * .82, Math.sin(e.wiggle) * .2, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = '#3b0b1d';
    ctx.beginPath(); ctx.arc(-e.r * .35, -e.r * .1, 2.5, 0, Math.PI * 2); ctx.arc(e.r * .35, -e.r * .1, 2.5, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,.45)'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(-e.r * .85, -e.r * .55); ctx.lineTo(-e.r * 1.35, -e.r * .95); ctx.moveTo(e.r * .85, -e.r * .55); ctx.lineTo(e.r * 1.35, -e.r * .95); ctx.stroke();
  }
  if (e.maxHp > 1) {
    ctx.shadowBlur = 0; ctx.strokeStyle = 'rgba(255,255,255,.62)'; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.arc(0, 0, e.r + 6, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * (e.hp / e.maxHp)); ctx.stroke();
  }
  ctx.restore();
}

function draw() {
  fit();
  ctx.save();
  if (shakePower > 0) { ctx.translate(rand(-shakePower, shakePower), rand(-shakePower, shakePower)); shakePower *= .82; if (shakePower < .2) shakePower = 0; }
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#182a46'); bg.addColorStop(.58, '#091726'); bg.addColorStop(1, '#06101b');
  ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
  ctx.globalAlpha = .32;
  ctx.strokeStyle = '#7ff7ff'; ctx.lineWidth = 1;
  for (let i = 0; i < 46; i++) {
    const x = (i * 47 + performance.now() * .025) % (W + 40) - 40;
    const y = (i * 89 + performance.now() * .16) % (H + 80) - 80;
    ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x - 10, y + 26); ctx.stroke();
  }
  ctx.globalAlpha = 1;

  const c = core();
  ctx.save();
  ctx.shadowColor = '#74f7ff'; ctx.shadowBlur = 26;
  ctx.fillStyle = '#143b4d'; ctx.beginPath(); ctx.arc(c.x, c.y, c.r, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#74f7ff'; ctx.beginPath(); ctx.arc(c.x, c.y, c.r * .55, 0, Math.PI * 2); ctx.fill();
  ctx.strokeStyle = st.shield ? '#ffd166' : 'rgba(255,255,255,.3)'; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(c.x, c.y, c.r + 12, 0, Math.PI * 2); ctx.stroke();
  ctx.restore();

  for (const b of st.bursts) {
    ctx.save(); ctx.globalAlpha = Math.max(0, b.life);
    ctx.strokeStyle = b.kind === 'core' ? '#ff8090' : b.kind === 'hit' ? '#ffd166' : '#74f7ff';
    ctx.lineWidth = b.kind === 'splash' ? 7 : 4;
    ctx.shadowColor = ctx.strokeStyle as string; ctx.shadowBlur = 22;
    ctx.beginPath(); ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2); ctx.stroke();
    ctx.restore();
  }
  for (const o of st.orbs) {
    ctx.save(); ctx.globalAlpha = .26; ctx.strokeStyle = '#aafcff'; ctx.lineWidth = 4;
    ctx.beginPath(); o.trail.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)); ctx.stroke(); ctx.restore();
    drawDrop(o.x, o.y, o.r, o.held);
  }
  for (const e of st.enemies) drawEnemy(e);
  for (const p of st.particles) { ctx.globalAlpha = Math.max(0, p.life); ctx.fillStyle = p.color; ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2); ctx.fill(); }
  ctx.globalAlpha = 1;
  for (const p of st.pops) { ctx.globalAlpha = Math.max(0, p.life); ctx.fillStyle = p.color; ctx.font = '900 15px Inter, system-ui'; ctx.textAlign = 'center'; ctx.fillText(p.text, p.x, p.y); }
  ctx.globalAlpha = 1;
  ctx.restore();
}

function loop(t: number) {
  if (!st) return;
  const dt = Math.min(0.033, (t - st.last) / 1000);
  st.last = t;
  update(dt);
  draw();
  if (st.phase !== 'over') requestAnimationFrame(loop);
}

function point(e: PointerEvent) { const r = canvas.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; }
function nearestOrb(p: { x: number; y: number }) {
  return st.orbs.filter(o => !o.held).map(o => ({ o, d: dist(o, p) })).sort((a, b) => a.d - b.d)[0];
}

canvas.addEventListener('pointerdown', e => {
  if (st.phase !== 'play') return;
  const p = point(e);
  const pick = nearestOrb(p);
  if (pick && pick.d < st.magnet) {
    st.held = pick.o;
    pick.o.held = true; pick.o.x = p.x; pick.o.y = p.y; pick.o.vx = 0; pick.o.vy = 0; pick.o.lastX = p.x; pick.o.lastY = p.y;
    canvas.setPointerCapture(e.pointerId);
    pop(p.x, p.y - 18, 'grab', '#bffbff');
    setText();
  }
});
canvas.addEventListener('pointermove', e => {
  if (st.phase !== 'play') return;
  const p = point(e);
  if (st.held) {
    const o = st.held;
    o.vx = (p.x - o.lastX) * 18;
    o.vy = (p.y - o.lastY) * 18;
    o.lastX = p.x; o.lastY = p.y; o.x += (p.x - o.x) * .8; o.y += (p.y - o.y) * .8;
  } else {
    const pick = nearestOrb(p);
    if (pick && pick.d < st.magnet * 0.52) {
      pick.o.vx += (p.x - pick.o.x) * 0.9;
      pick.o.vy += (p.y - pick.o.y) * 0.9;
    }
  }
});
canvas.addEventListener('pointerup', e => {
  if (!st.held) return;
  const o = st.held;
  o.held = false;
  const m = Math.hypot(o.vx, o.vy) || 1;
  const min = 260;
  if (m < min) { o.vx = o.vx / m * min; o.vy = o.vy / m * min; }
  o.vx = Math.max(-760, Math.min(760, o.vx));
  o.vy = Math.max(-760, Math.min(760, o.vy));
  st.held = undefined;
  pop(o.x, o.y - 16, 'throw', '#bffbff');
  setText();
  try { canvas.releasePointerCapture(e.pointerId); } catch {}
});
canvas.addEventListener('pointercancel', () => { if (st.held) { st.held.held = false; st.held = undefined; } });

addEventListener('resize', () => { fit(); draw(); });
$('#start').onclick = init;
$('#again').onclick = init;
$('#restart').onclick = init;
fit();
